import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDdrmsViewDocs, loadDdrmsViewDocs } from "../../store/documents";

const ToolbarButton = ({ children, onClick, title }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      border: "none",
      background: "transparent",
      padding: "6px 10px",
      margin: "0 4px",
      color: "#eee",
      borderRadius: 6,
      cursor: "pointer",
    }}
    onMouseDown={(e) => e.preventDefault()} // avoid selecting text while dragging nearby
  >
    {children}
  </button>
);

const ViewFile = ({ comp }) => {
  const wrapperRef = useRef(null);
  const startYRef = useRef(0);
  const startHRef = useRef(0);

  const [height, setHeight] = useState(200);
  const [isResizing, setIsResizing] = useState(false);
  const [viewerSrc, setViewerSrc] = useState("");

  const ddrmsViewDocs = useSelector(getDdrmsViewDocs);
  const dispatch = useDispatch();

  useEffect(() => {
    if (comp?.ddrmsDoc > 0) {
      dispatch(loadDdrmsViewDocs(comp.ddrmsDoc));
    }
  }, [comp?.ddrmsDoc, dispatch]);

  useEffect(() => {
    if (!ddrmsViewDocs) {
      setViewerSrc("");
      return undefined;
    }

    let objectUrl = "";
    let isCancelled = false;

    const setWithToolbar = (url) => {
      if (!isCancelled) {
        setViewerSrc(`${url}#toolbar=0&navpanes=0&scrollbar=0`);
      }
    };

    const resolveViewer = async () => {
      // Case 1: API middleware returns Blob (older behavior).
      if (ddrmsViewDocs instanceof Blob) {
        objectUrl = URL.createObjectURL(new Blob([ddrmsViewDocs], { type: "application/pdf" }));
        setWithToolbar(objectUrl);
        return;
      }

      // Case 2/3: API returns URL string or object with uploadUrl.
      const sourceUrl =
        typeof ddrmsViewDocs === "string"
          ? ddrmsViewDocs
          : ddrmsViewDocs?.uploadUrl || "";

      if (!sourceUrl) {
        setViewerSrc("");
        return;
      }

      try {
        // Fetch as blob to avoid forced download behavior from remote headers.
        const response = await fetch(sourceUrl);
        if (!response.ok) throw new Error("Unable to load PDF");
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setWithToolbar(objectUrl);
      } catch (error) {
        // Fallback to direct URL if blob fetch fails.
        setWithToolbar(sourceUrl);
      }
    };

    resolveViewer();

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ddrmsViewDocs]);

  // Drag-to-resize (listen on window)
  useEffect(() => {
    const onMove = (e) => {
      if (!isResizing || !wrapperRef.current) return;
      const delta = e.clientY - startYRef.current;
      const next = Math.max(240, startHRef.current + delta); // min height
      setHeight(next);
    };
    const onUp = () => setIsResizing(false);

    if (isResizing) {
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    }
    return () => {
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isResizing]);

  const startResize = (e) => {
    if (!wrapperRef.current) return;
    setIsResizing(true);
    startYRef.current = e.clientY;
    startHRef.current = wrapperRef.current.getBoundingClientRect().height;
  };

  // Toolbar actions
  const setPercent = (p) => {
    const vh = window.innerHeight || 800;
    setHeight(Math.round((vh * p) / 100));
  };
  const fitToViewport = () => setPercent(100);
  const minus = () => setPercent(Math.max(10, Math.round((height / window.innerHeight) * 100) - 10));
  const plus = () => setPercent(Math.min(150, Math.round((height / window.innerHeight) * 100) + 10));

  const currentPercent = Math.round((height / (window.innerHeight || height)) * 100);

  if (comp?.attributeDataTypes !== "pdf") return null;

  return (
    <div onContextMenu={(e) => e.preventDefault()} className="form-group">
      {/* Top toolbar */}
      <div
        style={{
          background: "#3a3a3a",
          color: "#fff",
          height: 48,
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
          gap: 6,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        {/* Page indicator stub (you can wire this up if you use pdf.js proper) */}
        <div style={{ opacity: 0.9, fontSize: 13, marginRight: 8 }}>
          <span style={{ padding: "4px 6px", background: "#2f2f2f", borderRadius: 4 }}>1</span>
          <span style={{ margin: "0 6px" }}>/</span>
          <span style={{ padding: "4px 6px", background: "#2f2f2f", borderRadius: 4 }}>?</span>
        </div>

        <ToolbarButton onClick={minus} title="Decrease height">-</ToolbarButton>

        <div style={{ borderLeft: "1px solid #555", height: 20, margin: "0 6px" }} />

        <ToolbarButton onClick={() => setPercent(25)} title="25% height">25%</ToolbarButton>
        <ToolbarButton onClick={() => setPercent(50)} title="50% height">50%</ToolbarButton>
        <ToolbarButton onClick={() => setPercent(100)} title="100% height">100%</ToolbarButton>

        <ToolbarButton onClick={plus} title="Increase height">+</ToolbarButton>

        <div style={{ borderLeft: "1px solid #555", height: 20, margin: "0 6px" }} />

        <ToolbarButton onClick={fitToViewport} title="Fit to viewport height">Fit</ToolbarButton>

        <div style={{ marginLeft: "auto", opacity: 0.9, fontSize: 13 }}>
          {currentPercent}%
        </div>
      </div>

      {/* Wrapper + PDF host */}
      <div
        ref={wrapperRef}
        style={{
          height,
          border: "1px solid #ccc",
          borderTop: "none",
          borderBottomLeftRadius: 6,
          borderBottomRightRadius: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: "100%",
            overflow: "hidden",
            background: "#2b2b2b",
          }}
        >
          {viewerSrc && (
            <iframe
              title="PDF Viewer"
              src={viewerSrc}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )}
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          style={{
            height: 10,
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            cursor: "ns-resize",
            background: "rgba(255,255,255,0.08)",
            boxShadow: "0 -1px 0 rgba(0,0,0,0.35) inset",
          }}
        />
      </div>
    </div>
  );
};

export default ViewFile;

